import React, { useState, useEffect } from 'react';
import { X, Building2, MapPin, Phone, CreditCard, AlertCircle, CheckCircle } from 'lucide-react';
import { api } from '../../services/api';
import { showSuccess, showError } from '../../utils/toast';

interface AddGarageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGarageAdded: () => void;
}

interface GarageForm {
  name: string;
  phone: string;
  address: string;
  city: string;
}

interface SubscriptionPlan {
  type: 'monthly' | 'semi_annual';
  name: string;
  price: number;
  duration: number;
  discount?: number;
}

interface BillingInfo {
  currentSubscription: {
    plan_type: string;
    amount: number;
    end_date: string;
    garages_count: number;
  };
  newPricing: {
    monthly_per_garage: number;
    prorated_amount: number;
    free_days: number;
    next_billing_date: string;
    total_monthly_cost: number;
  };
}

const AddGarageModal: React.FC<AddGarageModalProps> = ({ isOpen, onClose, onGarageAdded }) => {
  const [formData, setFormData] = useState<GarageForm>({
    name: '',
    phone: '',
    address: '',
    city: 'Kuwait City'
  });
  
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'semi_annual'>('monthly');
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [step, setStep] = useState<'form' | 'billing' | 'confirmation'>('form');

  const plans: SubscriptionPlan[] = [
    {
      type: 'monthly',
      name: 'Monthly Plan',
      price: 60,
      duration: 1
    },
    {
      type: 'semi_annual',
      name: 'Semi-Annual Plan',
      price: 300,
      duration: 6,
      discount: 1 // 1 month free
    }
  ];

  useEffect(() => {
    if (isOpen && step === 'billing') {
      fetchBillingInfo();
    }
  }, [isOpen, step, selectedPlan]);

  const fetchBillingInfo = async () => {
    setLoadingBilling(true);
    try {
      const response = await api.get(`/garages/billing-preview?plan_type=${selectedPlan}`);
      setBillingInfo(response.data);
    } catch (error) {
      showError('Failed to load billing information');
      console.error('Error fetching billing info:', error);
    } finally {
      setLoadingBilling(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      showError('Garage name is required');
      return false;
    }
    if (!formData.phone.trim()) {
      showError('Phone number is required');
      return false;
    }
    if (!formData.address.trim()) {
      showError('Address is required');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 'form') {
      if (validateForm()) {
        setStep('billing');
      }
    } else if (step === 'billing') {
      setStep('confirmation');
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...formData,
        plan_type: selectedPlan
      };

      const response = await api.post('/garages/add-with-subscription', payload);
      
      showSuccess('Garage added successfully! Subscription updated.');
      onGarageAdded();
      onClose();
      resetForm();
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to add garage';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      address: '',
      city: 'Kuwait City'
    });
    setStep('form');
    setBillingInfo(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-2">
            <Building2 className="w-6 h-6 text-[#008B8B]" />
            <h2 className="text-xl font-semibold text-gray-900">Add New Garage</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Indicators */}
        <div className="flex items-center justify-center p-4 border-b">
          <div className="flex items-center space-x-4">
            <div className={`flex items-center space-x-2 ${step === 'form' ? 'text-[#008B8B]' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step === 'form' ? 'bg-[#008B8B] text-white' : 
                ['billing', 'confirmation'].includes(step) ? 'bg-green-500 text-white' : 'bg-gray-200'
              }`}>
                1
              </div>
              <span className="text-sm font-medium">Garage Details</span>
            </div>
            <div className="w-8 h-px bg-gray-300" />
            <div className={`flex items-center space-x-2 ${step === 'billing' ? 'text-[#008B8B]' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step === 'billing' ? 'bg-[#008B8B] text-white' : 
                step === 'confirmation' ? 'bg-green-500 text-white' : 'bg-gray-200'
              }`}>
                2
              </div>
              <span className="text-sm font-medium">Billing & Plan</span>
            </div>
            <div className="w-8 h-px bg-gray-300" />
            <div className={`flex items-center space-x-2 ${step === 'confirmation' ? 'text-[#008B8B]' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step === 'confirmation' ? 'bg-[#008B8B] text-white' : 'bg-gray-200'
              }`}>
                3
              </div>
              <span className="text-sm font-medium">Confirmation</span>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Step 1: Garage Details Form */}
          {step === 'form' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Garage Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
                  placeholder="Enter garage name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
                  placeholder="+965 XXXX XXXX"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address *
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
                  placeholder="Enter full address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
                  placeholder="Kuwait City"
                />
              </div>
            </div>
          )}

          {/* Step 2: Billing & Plan Selection */}
          {step === 'billing' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Choose Subscription Plan</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {plans.map((plan) => (
                    <div
                      key={plan.type}
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                        selectedPlan === plan.type
                          ? 'border-[#008B8B] bg-[#008B8B]/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setSelectedPlan(plan.type)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900">{plan.name}</h4>
                        <div className={`w-4 h-4 rounded-full border-2 ${
                          selectedPlan === plan.type
                            ? 'border-[#008B8B] bg-[#008B8B]'
                            : 'border-gray-300'
                        }`}>
                          {selectedPlan === plan.type && (
                            <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5" />
                          )}
                        </div>
                      </div>
                      <div className="text-2xl font-bold text-gray-900 mb-1">
                        {plan.price} KWD
                      </div>
                      <div className="text-sm text-gray-500 mb-2">
                        per {plan.duration} month{plan.duration > 1 ? 's' : ''}
                      </div>
                      {plan.discount && (
                        <div className="text-sm text-green-600 font-medium">
                          Save {plan.discount} month free!
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Billing Information */}
              {loadingBilling ? (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  </div>
                </div>
              ) : billingInfo && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-blue-900 mb-2">Billing Information</h4>
                      <div className="text-sm text-blue-800 space-y-1">
                        <p>Current garages: {billingInfo.currentSubscription.garages_count}</p>
                        <p>Monthly cost per garage: {billingInfo.newPricing.monthly_per_garage} KWD</p>
                        <p>Free trial period: {billingInfo.newPricing.free_days} days</p>
                        <p>Next billing date: {new Date(billingInfo.newPricing.next_billing_date).toLocaleDateString()}</p>
                        <p className="font-medium">
                          New total monthly cost: {billingInfo.newPricing.total_monthly_cost} KWD
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Confirmation */}
          {step === 'confirmation' && (
            <div className="space-y-6">
              <div className="text-center">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Ready to Add Garage</h3>
                <p className="text-gray-600">Please review the details below before confirming.</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Garage Name:</span>
                  <span className="font-medium">{formData.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Phone:</span>
                  <span className="font-medium">{formData.phone}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Address:</span>
                  <span className="font-medium">{formData.address}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">City:</span>
                  <span className="font-medium">{formData.city}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Plan:</span>
                  <span className="font-medium">
                    {plans.find(p => p.type === selectedPlan)?.name}
                  </span>
                </div>
              </div>

              {billingInfo && (
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-green-900 mb-2">Free Trial Benefits</h4>
                      <div className="text-sm text-green-800">
                        <p>🎉 Get {billingInfo.newPricing.free_days} days free to unify your billing cycle!</p>
                        <p>Your next payment will be on {new Date(billingInfo.newPricing.next_billing_date).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t">
          <button
            onClick={step === 'form' ? handleClose : () => setStep(step === 'billing' ? 'form' : 'billing')}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {step === 'form' ? 'Cancel' : 'Back'}
          </button>
          
          <button
            onClick={step === 'confirmation' ? handleSubmit : handleNext}
            disabled={loading || (step === 'billing' && loadingBilling)}
            className="px-6 py-2 bg-[#008B8B] text-white rounded-lg hover:bg-[#007A7A] disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            <span>
              {step === 'confirmation' ? 'Add Garage' : 'Next'}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddGarageModal;